import classNames from "classnames";
import React from "react";
import { useTranslation } from "react-i18next";

import RedirectToView from "components/icons/RedirectToView";
import { createProductIdToNameIbrMap, createProductIdToNameMap } from "components/licenses/common";
import styles from "components/licenses/entitlements/entitlement.scss";
import mcsStyles from "components/mcs/mcs.scss";
import { deriveEntitlementState } from "components/mcs/McsTable";
import Modal from "components/modal/Modal";
import { ALL_MCS_ROUTE } from "components/router/Routes";
import StatusBadge, { Status } from "components/status-badge/StatusBadge";
import StaticTable from "components/support/api-guide/StaticTable";
import Heading from "components/typography/heading/Heading";
import { EntitlementLicenseDto, EntitlementState, EntitlementType } from "domain/licenses";
import form from "styles/form.scss";
import { formatDateWithoutTime } from "utils/format";
import { useTheme } from "utils/useTheme";

interface EntitlementDetailsModalProps {
    isOpen: boolean;
    onClose: () => void;
    entitlementDetails: {
        type: string;
        id: string;
        container: string | null;
        description: string;
        mcName: string;
        creationDate: string;
        licenses: EntitlementLicenseDto[] | null;
        state: EntitlementState | null;
        activationDate?: string;
    } | null;
}

export const ENTITLEMENT_STATE_MAPPING: Record<EntitlementState, Status> = {
    [EntitlementState.ACTIVATED]: Status.SUCCESS,
    [EntitlementState.IN_PROGRESS]: Status.WARNING,
    [EntitlementState.WAITING_FOR_C2V]: Status.WARNING,
    [EntitlementState.FAILED]: Status.ERROR,
    [EntitlementState.MANUALLY_ACTIVATED]: Status.SUCCESS,
};

const EntitlementDetailsModal: React.FC<EntitlementDetailsModalProps> = ({ isOpen, onClose, entitlementDetails }) => {
    const { t } = useTranslation();
    const PRODUCT_ID_TO_NAME = createProductIdToNameMap();
    const PRODUCT_ID_IBR_TO_NAME = createProductIdToNameIbrMap();
    const theme = useTheme();
    const getEntitlementTypeTranslation = (type: string): string => {
        switch (type) {
            case EntitlementType.HL_UPDATE:
                return t("Entitlements.type.hlUpdate");
            case EntitlementType.SL_UPDATE:
                return t("Entitlements.type.slUpdate");
            case EntitlementType.SL_CREATE:
                return t("Entitlements.type.slCreate");
            default:
                return type;
        }
    };
    const extractBlanccoProductId = (productId: string): string => {
        for (const [key, value] of PRODUCT_ID_IBR_TO_NAME) {
            if (value === productId) {
                return key;
            }
        }
        return productId;
    };

    if (!entitlementDetails) {
        return null;
    }
    const displayValue = (value: string | null) => value || t("Entitlements.details.na");
    const displayDate = (dateString: string) => {
        return dateString ? formatDateWithoutTime(dateString) : t("Entitlements.details.na");
    };

    function createLicenseName(licenseId: number) {
        const productId = extractBlanccoProductId(licenseId.toString());
        return PRODUCT_ID_TO_NAME.get(productId) ?? t("Entitlements.details.na");
    }

    const licenseCells =
        entitlementDetails.licenses?.map((license) => [
            createLicenseName(license.license_id),
            String(license.amount),
            displayDate(license.expiration_date),
        ]) || [];

    return (
        <Modal isOpen={isOpen} hideModal={onClose} modalTitle="">
            <div>
                <Heading tag="div" variant="SUBTITLE_1" disableTopSpacing={true}>
                    {entitlementDetails.id}
                </Heading>
                <Heading tag="div" variant="SUBTITLE_1" disableTopSpacing={false}>
                    <strong>{t("Entitlements.details.title")}</strong>
                </Heading>
                <div className={form.formFields}>
                    <span className={form.label}>{t("Entitlements.details.deliveredTo")} </span>
                    {entitlementDetails.mcName || t("Entitlements.details.na")}
                    {entitlementDetails.mcName && (
                        <button
                            className={mcsStyles.entitlementViewButton}
                            onClick={() => window.location.replace(ALL_MCS_ROUTE.path)}
                        >
                            <RedirectToView color={theme.iconFillColor} />
                        </button>
                    )}
                </div>
                <div className={form.formFields}>
                    <span className={form.label}>{t("Entitlements.details.type")} </span>
                    {getEntitlementTypeTranslation(entitlementDetails.type)}
                </div>
                <div className={form.formFields}>
                    <span className={form.label}>{t("Entitlements.details.container")} </span>
                    {displayValue(entitlementDetails.container)}
                </div>
                <div className={classNames(form.formFields, styles.entitlementState)}>
                    <span className={form.label}>{t("Entitlements.details.state")} </span>
                    {entitlementDetails.state ? (
                        <StatusBadge
                            values={[
                                deriveEntitlementState(
                                    t,
                                    entitlementDetails.state,
                                    entitlementDetails.activationDate,
                                    false
                                ),
                            ]}
                            tooltip={true}
                        />
                    ) : (
                        t("Entitlements.details.na")
                    )}
                </div>

                <div className={form.formFields}>
                    <span className={form.label}>{t("Entitlements.details.description")} </span>
                    {displayValue(entitlementDetails.description)}
                </div>
                <div className={form.formFields}>
                    <span className={form.label}>{t("Entitlements.details.creationDate")} </span>
                    {displayDate(entitlementDetails.creationDate)}
                </div>
                <Heading tag="div" variant="SUBTITLE_1" disableTopSpacing={false}>
                    <strong>{t("Entitlements.details.licencetitle")} </strong>
                </Heading>
                <div>{t("Entitlements.details.licencedetail")}</div>
                <StaticTable
                    headers={[
                        { value: t("Entitlements.details.license") },
                        { value: t("Entitlements.details.amount") },
                        { value: t("Entitlements.details.expirationDate") },
                    ]}
                    cells={licenseCells}
                />
            </div>
        </Modal>
    );
};

export default EntitlementDetailsModal;
